/*
    What kind of file this is, said in a way somebody recognises at a glance.

    The list used to show a three-letter extension in a box: `XLSX`, `DOCX`, `JPEG`. That is the
    file's suffix, not its meaning — and it is the least readable part of the name, in the smallest
    type on the screen. Somebody scanning six attachments wants to find "the spreadsheet" and "the
    photo", not to parse six abbreviations.

    So each file gets a kind, a short human label, and a colour. The kind decides whether a
    thumbnail is worth fetching; the label and colour do the work when it is not.

    Media type first, extension second. The media type is what the sender's client declared and
    what our own server will honour; the extension is a hint on the end of a name that anybody can
    type anything into. They usually agree, and when they do not, the declared type is the one the
    rest of the system acts on.
*/

export type FileKind = 'image' | 'pdf' | 'sheet' | 'document' | 'slides' | 'archive' | 'text' | 'file';

export interface FileLook {
    kind: FileKind;
    /** Three or four characters, upper case, shown when there is no thumbnail. */
    label: string;
    /** A colour that survives both themes. Kinds people sort by get a distinct one. */
    tint: string;
}

const BY_MEDIA: Record<string, FileKind> = {
    'application/pdf': 'pdf',
    'application/vnd.ms-excel': 'sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'sheet',
    'application/vnd.oasis.opendocument.spreadsheet': 'sheet',
    'text/csv': 'sheet',
    'application/msword': 'document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
    'application/vnd.oasis.opendocument.text': 'document',
    'application/rtf': 'document',
    'application/vnd.ms-powerpoint': 'slides',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'slides',
    'application/vnd.oasis.opendocument.presentation': 'slides',
    'application/zip': 'archive',
    'application/x-7z-compressed': 'archive',
    'application/x-rar-compressed': 'archive',
    'application/gzip': 'archive',
    'application/x-tar': 'archive',
};

const BY_EXTENSION: Record<string, FileKind> = {
    pdf: 'pdf',
    xls: 'sheet', xlsx: 'sheet', ods: 'sheet', csv: 'sheet',
    doc: 'document', docx: 'document', odt: 'document', rtf: 'document',
    ppt: 'slides', pptx: 'slides', odp: 'slides',
    zip: 'archive', rar: 'archive', '7z': 'archive', gz: 'archive', tar: 'archive',
    txt: 'text', log: 'text', md: 'text',
    html: 'text', htm: 'text', xml: 'text', json: 'text',
};

const TINTS: Record<FileKind, string> = {
    image: '#6E97BB',
    pdf: '#C0392B',
    sheet: '#2E8B57',
    document: '#2C6CB0',
    slides: '#C8500F',
    archive: '#7A6A55',
    text: '#5A6472',
    file: '#5A6472',
};

/** The extension, lower case, or empty when the name has none worth reading. */
export function extensionOf(name: string | null | undefined): string {
    if (!name) return '';
    const at = name.lastIndexOf('.');
    if (at <= 0 || at === name.length - 1) return '';
    const ext = name.slice(at + 1).toLowerCase();
    // A "extension" longer than this is part of the name, not a type — "Rapor v1.2 son".
    return /^[a-z0-9]{1,5}$/.test(ext) ? ext : '';
}

export function lookOf(mediaType: string | null | undefined, name: string | null | undefined): FileLook {
    const media = (mediaType ?? '').toLowerCase();
    const ext = extensionOf(name);

    let kind: FileKind = 'file';
    if (media.startsWith('image/')) kind = 'image';
    else if (BY_MEDIA[media]) kind = BY_MEDIA[media]!;
    else if (media.startsWith('text/')) kind = 'text';
    else if (BY_EXTENSION[ext]) kind = BY_EXTENSION[ext]!;

    // The extension is the better label when there is one: people know "XLSX" means the
    // spreadsheet they were sent, and "SHEET" is a word this app made up.
    const label = (ext || media.split('/').pop() || 'dosya').slice(0, 4).toUpperCase();
    return { kind, label, tint: TINTS[kind] };
}

/*
    Whether a real picture of the file is worth showing.

    Only for images, and only for the ones the server will serve for display — asking for anything
    else gets a download, and a card that quietly downloads a 5 MB file to draw a 64-pixel square
    is a card that costs somebody their data plan.
*/
const THUMBNAILABLE = new Set([
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/bmp',
]);

/** Big pictures are still fetched whole — there is no server-side resizer — so cap it. */
export const THUMBNAIL_LIMIT = 2 << 20;

export function thumbnailable(mediaType: string | null | undefined, size: number): boolean {
    return THUMBNAILABLE.has((mediaType ?? '').toLowerCase()) && size <= THUMBNAIL_LIMIT;
}
